import { useEffect, useState } from 'react';
import databaseMonitorService, { SchemaInfo } from '../../../../../services/databaseMonitor.service';

export function SchemasView({ serverId }: { serverId: string }) {
  const [schemas, setSchemas] = useState<SchemaInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openSchema, setOpenSchema] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setSchemas([]);
    databaseMonitorService
      .getSchemas(serverId, controller.signal)
      .then((d) => {
        setSchemas(d);
        setLoading(false);
      })
      .catch((err: any) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load schemas');
        setLoading(false);
      });
    return () => controller.abort();
  }, [serverId]);

  if (loading) return <div className="text-slate-600 dark:text-slate-400">Loading…</div>;
  if (error) return <div className="text-red-600 dark:text-red-400">Error: {error}</div>;

  if (schemas.length === 0) {
    return <div className="text-slate-600 dark:text-slate-400">No user schemas.</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      {schemas.map((s) => (
        <SchemaRow
          key={s.name}
          schema={s}
          open={openSchema === s.name}
          onToggle={() => setOpenSchema(openSchema === s.name ? null : s.name)}
        />
      ))}
    </div>
  );
}

function SchemaRow({
  schema,
  open,
  onToggle,
}: {
  schema: SchemaInfo;
  open: boolean;
  onToggle: () => void;
}) {
  const totalBytes = schema.tables.reduce(
    (a, t) => a + (t.data_bytes ?? 0) + (t.index_bytes ?? 0),
    0
  );
  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-md">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-900/60"
      >
        <div>
          <div className="font-medium text-slate-900 dark:text-slate-100">{schema.name}</div>
          <div className="text-xs text-slate-500 dark:text-slate-500">
            {schema.tables.length} tables · {formatBytes(totalBytes)}
            {schema.charset ? ` · ${schema.charset}` : ''}
          </div>
        </div>
        <span className="text-slate-500 dark:text-slate-500">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-slate-200 dark:border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 text-xs">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium">Table</th>
                <th className="px-3 py-1.5 text-left font-medium">Engine</th>
                <th className="px-3 py-1.5 text-right font-medium">Rows</th>
                <th className="px-3 py-1.5 text-right font-medium">Data</th>
                <th className="px-3 py-1.5 text-right font-medium">Index</th>
              </tr>
            </thead>
            <tbody>
              {schema.tables.map((t) => (
                <tr key={t.name} className="border-t border-slate-100 dark:border-slate-900 text-slate-900 dark:text-slate-100">
                  <td className="px-3 py-1">{t.name}</td>
                  <td className="px-3 py-1 text-slate-600 dark:text-slate-400">{t.engine ?? '—'}</td>
                  <td className="px-3 py-1 text-right">
                    {t.rows != null ? t.rows.toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-1 text-right">{formatBytes(t.data_bytes)}</td>
                  <td className="px-3 py-1 text-right">{formatBytes(t.index_bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatBytes(n: number | null | undefined): string {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default SchemasView;
